import { FEATURE_FLAGS } from "@/data/globals";
import {
  IUnit,
  IUpgradeGains,
  IUpgradeGainsWeapon,
  IUpgradeOption,
  IUpgradePackage,
  IUpgradeSection,
  UpgradeError
} from "@/data/types";
import { isFeatureEnabled } from "@/env";
import { maxBy, orderBy, sortBy, sumBy } from "lodash-es";
import pluralize from "pluralize";
import { ArmyBookRule } from "../armyBook";
import ArmyBookUpgradeOption from "../armyBook/ArmyBookUpgradeOption";
import DataParsingService from "../DataParsingService";
import EquipmentService from "../EquipmentService";
import { forEachTarget, getUnitWeapons, isItem, makeCopy } from "../Helpers";
import { ILogger } from "../logging/ILogger";
import createLogger from "../logging/LoggerFactory";
import UpgradeService from "../UpgradeService";
import CalcService from "./calcService";
import { SPECIAL_SETTINGS } from "./Calculator";

const ENABLE_V36 = isFeatureEnabled(FEATURE_FLAGS.V3_6);

export class UpgradeCalcService {
  private logger: ILogger;
  constructor(public calcService: CalcService) {
    this.logger = createLogger(true);
  }

  public recalculateUpgradePackage(
    upgradePackage: IUpgradePackage,
    units: IUnit[],
    otherPackages: IUpgradePackage[]
  ) {
    for (const section of upgradePackage.sections) {
      try {
        const upgradeSection = DataParsingService.parseUpgradeText(section.label);

        upgradeSection.uid = section.uid;

        for (const optionIndex in section.options) {
          section.options[optionIndex] = this.recalculateUpgradeOption(
            upgradePackage,
            upgradeSection,
            section.options[optionIndex],
            units,
            otherPackages
          );
        }

        section.options = sortBy(
          section.options,
          (x) => maxBy(x.costs, (c: any) => c.exactCost || c.cost)?.cost ?? 0
        );
      } catch (err) {
        console.error("Could not parse upgrade section", section, err);
        debugger;
      }
    }

    let errorReason = UpgradeError.Unknown;
    for (const section of upgradePackage.sections) {
      for (const option of section.options) {
        for (const foo of option.proposedCostHint ?? []) {
          errorReason |= foo.error;
        }
      }
    }

    upgradePackage.calcErrors = errorReason > 0;
    upgradePackage.calcErrorReason = errorReason;

    return upgradePackage;
  }

  private recalculateUpgradeOption(
    upgradePackage: IUpgradePackage,
    upgradeSection: IUpgradeSection,
    option: IUpgradeOption,
    units: IUnit[],
    otherPackages: IUpgradePackage[]
  ) {
    const upgradeCostSet = units.map((origin) =>
      this.getUpgradeCostForUnit(origin, upgradePackage, upgradeSection, option, otherPackages)
    );

    const sortedGains = ArmyBookUpgradeOption.FromObject(option).gains;
    const isQuestConsumablePackage = upgradePackage.hint.includes("Consumables Shop");
    const questCostMult = isQuestConsumablePackage
      ? 1
      : this.calcService.calcSettings[SPECIAL_SETTINGS.QUEST_VALUES]?.["UpgradeMult"] || 1;

    return {
      ...option,
      gains: sortedGains,
      costs: upgradeCostSet.map((x) => ({
        unitId: x.unitId,
        cost: x.cost,
        exactCost: parseFloat(x.newCostPrecise.toFixed(2)),
        questCost: CalcService.round(x.newCostPrecise * questCostMult)
      })),
      proposedCostHint: upgradeCostSet.map((x) => ({
        unitName: x.unitName,
        newCostPrecise: x.newCostPrecise.toFixed(2),
        newCostRounded: x.cost,
        error: x.error
      }))
    };
  }

  public getUpgradeCostForUnit(
    origin: IUnit,
    upgradePackage: IUpgradePackage,
    upgradeSection: IUpgradeSection,
    option: IUpgradeOption,
    otherPackages: IUpgradePackage[] = []
  ) {
    const build = (unit: IUnit) => {
      return UpgradeService.buildUpgrades({
        ...unit,
        selectedUpgrades: [],
        loadout: []
      });
    };

    let unitSize = origin.size;
    const isAffectsAll = upgradeSection.affects?.type === "all" || upgradeSection.select?.type === "all";
    if (!isAffectsAll) {
      unitSize = 1;
    }

    const unit: IUnit = build(CalcService.formatUnit({ ...origin, size: unitSize, fullSize: origin.size }));

    // Normalize weapons
    const weapons = getUnitWeapons(unit);
    for (const weapon of weapons) {
      weapon.name = pluralize.singular(weapon.name || weapon.label);
      weapon.melee = !((weapon as IUpgradeGainsWeapon).range > 0);
      weapon.specialRules = weapon.specialRules.map((sr) => ArmyBookRule.Parse(sr) as any);
    }

    const cloneWithPrereqs = build(makeCopy(unit));
    const circuitBreaker = { iterations: 0 };
    const isValid = this.applyPrerequisites(
      cloneWithPrereqs,
      upgradeSection,
      upgradePackage,
      circuitBreaker,
      otherPackages
    );

    if (!isValid) {
      // ERROR
      this.logger?.error(`Could not apply prereqs`); // TODO: Better error
      return {
        unitId: unit.id,
        unitName: unit.name,
        newCostPrecise: 0,
        newCostRounded: 0,
        error: UpgradeError.Prerequisites
      };
    }

    this.logger?.log("Unit without upgrade", cloneWithPrereqs);

    const cloneWithUpgrade = makeCopy(cloneWithPrereqs);

    UpgradeService.apply(cloneWithUpgrade, upgradeSection, option);

    this.logger?.log("Unit with upgrade", cloneWithUpgrade);

    const baseCost = this.calcService.calculateUnitCost(cloneWithPrereqs);
    const upgradedCost = this.calcService.calculateUnitCost(cloneWithUpgrade);

    const newCostPrecise = upgradedCost - baseCost;
    const newCostRounded = CalcService.round(newCostPrecise);
    const cost = newCostPrecise > 0 ? Math.max(5, newCostRounded) : newCostRounded;

    return {
      unitId: unit.id,
      unitName: unit.name,
      newCostPrecise,
      newCostRounded,
      cost,
      error: cost === 0 ? UpgradeError.Free : newCostPrecise < 0 ? UpgradeError.Negative : undefined
    };
  }

  public applyPrerequisites(
    unit: ICalcUnit,
    targetSection: IUpgradeSection,
    upgradePackage: IUpgradePackage,
    circuitBreaker: { iterations: number },
    otherPackages: IUpgradePackage[] = []
  ) {
    // Don't need to apply anything...
    if (this.checkPrerequisites(unit, targetSection)) {
      return true;
    }

    const { section, option } = this.findMissingPrerequisites(
      unit,
      targetSection,
      upgradePackage.sections.concat(otherPackages.flatMap((x) => x?.sections ?? []))
    );

    // If we can't find the pre-reqs... problem
    if (option === null) {
      this.logger?.log(`Could not find prereqs for ${targetSection.label}`);
      return false;
    }

    circuitBreaker.iterations++;

    // Ensure that the dependency has its own dependencies applied
    if (
      circuitBreaker.iterations > 10 ||
      !this.applyPrerequisites(unit, section, upgradePackage, circuitBreaker, otherPackages)
    ) {
      return false;
    }

    UpgradeService.apply(unit, section, option);

    this.logger?.log("pre-reqs applied", unit);

    return true;
  }

  private checkPrerequisites(unit: IUnit, section: IUpgradeSection) {
    this.logger?.debug("checkPrerequisites", section);
    if (!section.targets) {
      return true;
    }

    const unitWeapons = getUnitWeapons(unit);
    let found = true;
    forEachTarget(section, (target) => {
      const weaponFound = EquipmentService.contains(unitWeapons, target);
      const itemFound = EquipmentService.contains(unit.items, target);

      found &&= weaponFound || itemFound;
    });
    return found;
  }

  public findMissingPrerequisites(
    unit: IUnit,
    targetSection: IUpgradeSection,
    searchSections: IUpgradeSection[]
  ) {
    this.logger?.debug("findMissingPrerequisites", targetSection);

    const otherSections = searchSections.filter((x) => x.uid !== targetSection.uid);

    for (const potentialSection of otherSections) {
      const fullSection = {
        ...DataParsingService.parseUpgradeText(potentialSection.label),
        ...potentialSection
      };
      const optionsThatSatisfy: IUpgradeOption[] = [];
      for (const potentialOption of fullSection.options) {
        const potentialGains = ArmyBookUpgradeOption.ExtractGains(potentialOption).concat(
          potentialOption.gains.filter((x) => isItem(x))
        );
        let satisfied = true;
        forEachTarget(targetSection, (target) => {
          const unitHasTarget = getUnitWeapons(unit).some((e) =>
            EquipmentService.compareEquipmentNames(target, e.name || e.label)
          );
          if (unitHasTarget) return;

          const gainsTarget = potentialGains.find((e) =>
            EquipmentService.compareEquipmentNames(target, e.name || e.label)
          );
          satisfied &&= !!gainsTarget;
        });

        if (satisfied) {
          optionsThatSatisfy.push(potentialOption);
          //return { section: fullSection, option: potentialOption };
        }
      }
      if (optionsThatSatisfy.length > 0) {
        // Order this by "power level" of the option
        // Where the power level is the attacks of all the targets summed together
        const orderedOptions = orderBy(optionsThatSatisfy, (x) => {
          try {
            const targetGains = x.gains.filter((gain) => {
              let match = false;
              // Is this gain one of the targets?
              forEachTarget(targetSection, (target) => {
                if (EquipmentService.compareEquipmentNames(target, gain.name || gain.label)) {
                  match = true;
                }
              });
              return match;
            });
            // Sum up the attacks of all
            return sumBy(targetGains, (target) => (target as any).attacks || 0);
          }
          // Fallback so that we don't break anything with cases that might not match the above
          catch (e) {
            return 1;
          }
        });

        return { section: fullSection, option: ENABLE_V36 ? orderedOptions[0] : optionsThatSatisfy[0] };
      }
    }
    return { section: null, option: null };
  }
}

interface ICalcUnit extends IUnit {
  loadout: IUpgradeGains[];
  selectedUpgrades: {
    instanceId: string;
    upgrade: IUpgradeSection;
    option: IUpgradeOption;
  }[];
}
